"use client";

import { useLingui } from "@lingui/react/macro";
import { useScrollLock } from "@offstreetparking/design-system/hooks";
import { OFFSTREET_COMPANY_LOGO } from "@offstreetparking/design-system/providers";
import { classNames } from "@offstreetparking/design-system/utils";
import { buildSupportUrl } from "@offstreetparking/portal-flow/utils/support-url";
import Image from "next/image";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EnvironmentBanner } from "~/components/EnvironmentBanner/EnvironmentBanner";
import { useParker } from "~/components/providers/AuthProvider";
import { useCompany } from "~/components/providers/CompanyProvider";
import { useKiosk } from "~/components/providers/KioskProvider";
import { LanguageChanger } from "~/components/providers/TranslationsProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { routes } from "~/routes";
import { MobileNav } from "./components/MobileNav";
import { MobileTopBar } from "./components/MobileTopBar";
import { Overlay } from "./components/Overlay";
import { TopBarNavLink } from "./components/TopBarNavLink";
import { TopBarUserDropdown } from "./components/TopBarUserDropdown";
export type TopBarProps = {
	companyLogo?: {
		url: string;
		altText: string;
	};
	isLoading?: boolean;
	eventId?: string;
	locationId?: string;
	tenantId?: number;
	hideTopBar?: boolean;
};
export const TopBar = ({
	companyLogo: rawCompanyLogo,
	isLoading: isInitialLoading,
	eventId,
	locationId,
	tenantId,
	hideTopBar = false,
}: TopBarProps) => {
	const { isKiosk } = useKiosk();
	const [menuOpen, setMenuOpen] = useState(false);
	const toggleButtonRef = useRef<HTMLButtonElement>(null);
	const closeMenu = useCallback(() => {
		setMenuOpen(false);
		toggleButtonRef.current?.focus();
	}, []);
	const toggleMenu = useCallback(() => {
		setMenuOpen((prev) => !prev);
	}, []);
	const { company } = useCompany();
	const parkerContext = useParker();
	const { portalAuthFlag, languageSwitcherFlag } = useFeatureFlags();
	const companyLogo = useMemo(() => {
		if (rawCompanyLogo) {
			return rawCompanyLogo;
		}
		if (!company?.branding?.companyLogoFileUrl) {
			return {
				url: OFFSTREET_COMPANY_LOGO,
				altText: "Offstreet",
			};
		}
		return {
			url: company.branding.companyLogoFileUrl,
			altText: company.name,
		};
	}, [company, rawCompanyLogo]);
	const isLoading = isInitialLoading && !company?.branding?.companyLogoFileUrl;
	useScrollLock({
		enabled: menuOpen && !isKiosk,
	});
	useEffect(() => {
		if (!menuOpen) {
			return;
		}
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				closeMenu();
			}
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [menuOpen, closeMenu]);
	const { t } = useLingui();
	const pathname = usePathname();
	const routeParams = useParams();
	const urlSearchParams = useSearchParams();
	const routeLocation =
		typeof routeParams.locationIdentifier === "string" ? routeParams.locationIdentifier : undefined;
	const routeIdentifier =
		typeof routeParams.locationLookupId === "string"
			? routeParams.locationLookupId
			: typeof routeParams.lookup === "string"
				? routeParams.lookup
				: undefined;
	const routeTenantId = typeof routeParams.tenantId === "string" ? Number(routeParams.tenantId) : undefined;
	const codeCheckHref = routes.codeCheck({
		location: urlSearchParams.get("location") ?? routeLocation,
		identifier: urlSearchParams.get("identifier") ?? routeIdentifier,
		tenant: Number(urlSearchParams.get("tenant")) || routeTenantId || tenantId || undefined,
		permitTypeId: Number(urlSearchParams.get("permitTypeId")) || undefined,
		settingId: Number(urlSearchParams.get("settingId")) || undefined,
	});
	const supportUrl = buildSupportUrl({ locationId, eventId });
	if (!portalAuthFlag) {
		return (
			<>
				{/* Main Top Bar */}
				<header className="sticky top-0 z-topBar hidden w-full border-neutral-faded border-b bg-white px-6 py-4 md:block print:hidden">
					<div className="mx-auto flex max-w-[1280px] flex-1 items-center justify-between">
						{!isLoading && (
							<Image
								priority
								src={companyLogo.url}
								width={0}
								height={0}
								sizes="300px"
								alt={companyLogo.altText}
								style={{
									maxHeight: "42px",
									width: "auto",
									maxWidth: "300px",
								}}
							/>
						)}
						{isLoading && (
							<span
								style={{
									width: 300,
									height: 42,
								}}
								className={classNames("flex animate-pulse bg-neutral-faded")}
							/>
						)}
						{!isKiosk && (
							<nav
								aria-label={t`Main navigation`}
								className="hidden items-center justify-end gap-8 md:flex"
							>
								<TopBarNavLink href={routes.home()}>
									{t({
										message: "Park",
										context: "nav",
										comment:
											"Top-nav destination label for the parking section; a noun, not the submit action.",
									})}
								</TopBarNavLink>
								<TopBarNavLink href={codeCheckHref} isActive={pathname === routes.codeCheck()}>
									{t`Code Check`}
								</TopBarNavLink>
								<TopBarNavLink
									href={routes.sessionLookup({
										eventId,
									})}
								>
									{t({
										message: "Session Lookup",
										comment:
											"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
									})}
								</TopBarNavLink>
								<TopBarNavLink
									href={supportUrl}
									target="_blank"
									aria-label={`${t`Help`} ${t`(opens in a new tab)`}`}
								>
									{t`Help`}
								</TopBarNavLink>
								{languageSwitcherFlag && <LanguageChanger />}
							</nav>
						)}
					</div>
				</header>

				{/* Mobile Top Bar */}
				{!isKiosk && (
					<>
						<div className="sticky top-0 z-topBar bg-white md:hidden print:hidden">
							<Overlay isVisible={menuOpen} onClick={closeMenu} />
							<MobileNav
								isVisible={menuOpen}
								eventId={eventId}
								locationId={locationId}
								codeCheckHref={codeCheckHref}
								isLoggedIn={false}
							/>
							<MobileTopBar
								ref={toggleButtonRef}
								companyLogo={companyLogo}
								isLoading={isLoading}
								isVisible={menuOpen}
								toggleMenu={toggleMenu}
							/>
						</div>
						{/* Mobile Spacer */}
						<div className="h-[53px] shrink-0 md:hidden print:hidden" />
					</>
				)}
				<EnvironmentBanner />
			</>
		);
	}
	return (
		<>
			{/* Main Top Bar */}
			<header className="sticky top-0 z-topBar hidden w-full border-neutral-faded border-b bg-white px-6 py-4 md:block print:hidden">
				<div className="mx-auto flex max-w-[1280px] flex-1 items-center justify-between">
					{!isLoading && (
						<Image
							priority
							src={companyLogo.url}
							width={0}
							height={0}
							sizes="300px"
							alt={companyLogo.altText}
							style={{
								maxHeight: "42px",
								width: "auto",
								maxWidth: "300px",
							}}
						/>
					)}
					{isLoading && (
						<span
							style={{
								width: 300,
								height: 42,
							}}
							className={classNames("flex animate-pulse bg-neutral-faded")}
						/>
					)}

					{!isKiosk && (
						<nav
							aria-label={t`Main navigation`}
							className={classNames(
								"hidden items-center justify-end gap-8 md:flex",
								parkerContext.isLoggedIn ? "ml-8 grow" : "",
							)}
						>
							<TopBarNavLink href={routes.permits()} isVisible={parkerContext.isLoggedIn}>
								{t`Permits`}
							</TopBarNavLink>
							<TopBarNavLink href={routes.transactions()} isVisible={parkerContext.isLoggedIn}>
								{t`Transactions`}
							</TopBarNavLink>
							<TopBarNavLink href={routes.home()} isVisible={true}>
								{t({
									message: "Park",
									context: "nav",
									comment:
										"Top-nav destination label for the parking section; a noun, not the submit action.",
								})}
							</TopBarNavLink>
							<TopBarNavLink href={codeCheckHref} isVisible={true}>
								{t`Code Check`}
							</TopBarNavLink>
							<TopBarNavLink
								href={routes.sessionLookup({
									eventId,
								})}
								isVisible={true}
							>
								{t({
									message: "Session Lookup",
									comment:
										"Parking session — one vehicle's stay, looked up by confirmation number. Not a login session.",
								})}
							</TopBarNavLink>
							<TopBarNavLink href={routes.login()} isVisible={!parkerContext.isLoggedIn}>
								{t({
									message: "Login",
									comment:
										"One-word nav label for the sign-in action, not a username field. Links to the login page.",
								})}
							</TopBarNavLink>
							{languageSwitcherFlag && <LanguageChanger />}
							{parkerContext.isLoggedIn && (
								<div className="flex grow justify-end">
									<TopBarUserDropdown />
								</div>
							)}
						</nav>
					)}
				</div>
			</header>

			{/* Mobile Top Bar */}
			{!isKiosk && (
				<>
					<div className="sticky top-0 z-topBar bg-white md:hidden print:hidden">
						<Overlay isVisible={menuOpen} onClick={closeMenu} />
						<MobileNav
							isVisible={menuOpen}
							eventId={eventId}
							locationId={locationId}
							codeCheckHref={codeCheckHref}
							{...parkerContext}
						/>
						{!hideTopBar && (
							<MobileTopBar
								ref={toggleButtonRef}
								companyLogo={companyLogo}
								isLoading={isLoading}
								isVisible={menuOpen}
								toggleMenu={toggleMenu}
							/>
						)}
					</div>
					{/* Mobile Spacer */}
					{!hideTopBar && <div className="h-[53px] shrink-0 md:hidden print:hidden" />}
				</>
			)}
			<EnvironmentBanner />
		</>
	);
};
